import { useState, useEffect } from "react";

const STORAGE_KEY = "stakingCalcValues";

const StakingPowerCalc = () => {
  // Load initial state from localStorage or fallback defaults
  const [numOfLands, setNumOfLands] = useState(() => {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return saved?.numOfLands ?? 0;
  });

  const [pixel, setPixel] = useState(() => {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return saved?.pixel ?? 100;
  });

  const [apr, setApr] = useState(() => {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return saved?.apr ?? 0;
  });

  const [rewardAmount, setRewardAmount] = useState("");
  const [rewardDays, setRewardDays] = useState("");

  const [result, setResult] = useState({
    stakingPower: 0,
    landBonus: 0,
    dailyReward: 0,
    weeklyReward: 0,
    monthlyReward: 0,
    yearlyReward: 0,
  });

  // Save to localStorage on change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ numOfLands, pixel, apr }));
  }, [numOfLands, pixel, apr]);

  // Recalculate staking power whenever inputs change
  useEffect(() => {
    const calculateStakingPower = () => {
      const pixelAmount = Number(pixel);
      const landsCount = Number(numOfLands);

      const landBonus = landsCount * Math.min(pixelAmount * 0.1, 100000);
      const stakingPower = pixelAmount + landBonus;

      const rewards = { daily: 0, weekly: 0, monthly: 0, yearly: 0 };
      if (apr > 0) {
        const aprValue = Number(apr);
        const yearly = stakingPower * (aprValue / 100);
        rewards.daily = yearly / 365;
        rewards.weekly = yearly / 52;
        rewards.monthly = yearly / 12;
        rewards.yearly = yearly;
      }

      setResult({
        stakingPower,
        landBonus,
        dailyReward: rewards.daily,
        weeklyReward: rewards.weekly,
        monthlyReward: rewards.monthly,
        yearlyReward: rewards.yearly,
      });
    };

    calculateStakingPower();
  }, [pixel, numOfLands, apr]);

  const calculateAPRFromReward = () => {
    const received = parseFloat(rewardAmount);
    const days = parseInt(rewardDays);
    if (isNaN(received) || isNaN(days) || days <= 0 || pixel <= 0 || numOfLands < 0) {
      return 0;
    }

    // Total staking power (including land bonus)
    const pixelAmount = Number(pixel);
    const landsCount = Number(numOfLands);
    const landBonus = landsCount * Math.min(pixelAmount * 0.1, 100000);
    const stakingPower = pixelAmount + landBonus;

    // Calculate yearly reward based on received reward and days
    const yearlyRewardEstimate = (received / days) * 365;

    // Calculate APR
    const aprEstimate = (yearlyRewardEstimate / stakingPower) * 100;
    return aprEstimate;
  };

  return (
    <div className="flex flex-col md:flex-row gap-4">
      <div className="calculator-style">
        <h2 className="calculator-title">Staking Power Calculator</h2>

        <div className="calculator-project">
          <div className="form-group">
            <label className="block mb-2 font-medium"># of Lands (Only for in-game staking)</label>
            <input
              type="number"
              min="0"
              value={numOfLands}
              onChange={(e) => setNumOfLands(Math.max(1, parseInt(e.target.value) || 0))}
              className="w-full p-2 border rounded calculator-form"
            />
          </div>

          <div className="form-group">
            <label className="block mb-2 font-medium">Amount of $PIXEL</label>
            <input
              type="number"
              min="0"
              value={pixel}
              onChange={(e) => setPixel(Math.max(0, parseInt(e.target.value) || 0))}
              className="w-full p-2 border rounded calculator-form"
            />
          </div>

          <div className="form-group">
            <label className="block mb-2 font-medium">APR (%)</label>
            <input
              type="number"
              min="0"
              step="0.1"
              value={apr}
              onChange={(e) => setApr(Math.max(0, parseFloat(e.target.value) || 0))}
              className="w-full p-2 border rounded calculator-form"
              placeholder="Enter APR to calculate rewards"
            />
          </div>

          <div className="mt-4 p-4 bg-[var(--panel-section-bg)] rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="calc-card">
                <div className="text-sm">Staking Power</div>
                <div className="text-base font-bold">{result.stakingPower.toLocaleString()} $PIXEL</div>
              </div>

              <div className="calc-card">
                <div className="text-sm">Land Bonus</div>
                <div className="text-base font-bold">{result.landBonus.toLocaleString()} $PIXEL</div>
              </div>

              {apr > 0 && (
                <>
                  <div className="calc-card">
                    <div className="text-sm">Daily Reward</div>
                    <div className="text-base font-bold">{Number(result.dailyReward.toFixed(2)).toLocaleString()} $PIXEL</div>
                  </div>
                  <div className="calc-card">
                    <div className="text-sm">Weekly Reward</div>
                    <div className="text-base font-bold">{Number(result.weeklyReward.toFixed(2)).toLocaleString()} $PIXEL</div>
                  </div>
                  <div className="calc-card">
                    <div className="text-sm">Monthly Reward</div>
                    <div className="text-base font-bold">{Number(result.monthlyReward.toFixed(2)).toLocaleString()} $PIXEL</div>
                  </div>
                  <div className="calc-card">
                    <div className="text-sm">Yearly Reward</div>
                    <div className="text-base font-bold">{Number(result.yearlyReward.toFixed(2)).toLocaleString()} $PIXEL</div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-style">
        <h2 className="calculator-title">Reverse Calculate APR</h2>
        <div className="calculator-project">
        <div className="form-group">
          <label className="block mb-2 font-medium">Received $PIXEL</label>
          <input
            type="number"
            min="0"
            value={rewardAmount}
            onChange={(e) => setRewardAmount(e.target.value)}
            className="w-full p-2 border rounded calculator-form"
            placeholder="Enter total received $PIXEL"
          />
        </div>

        <div className="form-group">
          <label className="block mb-2 font-medium">Number of Days</label>
          <input
            type="number"
            min="1"
            value={rewardDays}
            onChange={(e) => setRewardDays(e.target.value)}
            className="w-full p-2 border rounded calculator-form"
            placeholder="Enter number of days"
          />
        </div>
        <div className="form-group">
            <label className="block mb-2 font-medium"># of Lands (Only for in-game staking)</label>
            <input
              type="number"
              min="0"
              value={numOfLands}
              onChange={(e) => setNumOfLands(Math.max(1, parseInt(e.target.value) || 0))}
              className="w-full p-2 border rounded calculator-form"
            />
          </div>

          <div className="form-group">
            <label className="block mb-2 font-medium">Amount of $PIXEL</label>
            <input
              type="number"
              min="0"
              value={pixel}
              onChange={(e) => setPixel(Math.max(0, parseInt(e.target.value) || 0))}
              className="w-full p-2 border rounded calculator-form"
            />
          </div>

        <div className="mt-4 p-4 bg-[var(--panel-section-bg)] rounded-lg">
          <div className="calc-card">
            <h3 className="mb-2 font-semibold">Estimated APR from Rewards</h3>
            <div className="text-lg font-bold">{calculateAPRFromReward().toFixed(2)}%</div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default StakingPowerCalc;
